import React, { useState, useEffect, useContext } from 'react';
import {
  collection,
  query,
  where,
  getDocs,
  doc,
  getDoc,
  limit,
} from 'firebase/firestore';
import { useNavigate } from 'react-router-dom';
import { db } from '../utils/firebaseConfig';
import {
  FiSearch,
  FiHash,
  FiUser,
  FiCalendar,
  FiInfo
} from 'react-icons/fi';
import { AuthContext } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Box,
  Card,
  CardBody,
  InputGroup,
  InputLeftElement,
  Input,
  Spinner,
  Text,
  Flex,
  Badge,
  Alert,
  AlertIcon
} from '@chakra-ui/react';

const SearchReports = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { currentUser, role } = useContext(AuthContext);
  const { isDarkMode } = useTheme();

  // Debounce the search input
  useEffect(() => {
    if (searchTerm.trim() === '') {
      setSearchResults([]);
      return;
    }

    const timeoutId = setTimeout(() => {
      handleSearch();
    }, 300); // 300ms debounce

    return () => clearTimeout(timeoutId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm]);

  const handleSearch = async () => {
    setLoading(true);
    try {
      const term = searchTerm.trim().toLowerCase();

      // For trainees, we only search their own reports
      if (role === 'trainee') {
        const isArchived = await checkIfUserIsArchived(currentUser.uid);
        if (isArchived) {
          setError('Your account is archived. You cannot access reports.');
          setSearchResults([]);
          setLoading(false);
          return;
        }

        const [dorsResults, eowrResults, osrResults] = await Promise.all([
          searchTraineeReports('dors', term),
          searchTraineeReports('endOfWeekReports', term),
          searchTraineeReports('officerSafetyReports', term),
        ]);

        const combinedResults = [...dorsResults, ...eowrResults, ...osrResults];
        setSearchResults(combinedResults);
      } else {
        // For admins and trainers, search by reportId and traineeName
        const [dorsResults, eowrResults, osrResults] = await Promise.all([
          searchInCollection('dors', term),
          searchInCollection('endOfWeekReports', term),
          searchInCollection('officerSafetyReports', term),
        ]);

        const combinedResults = [...dorsResults, ...eowrResults, ...osrResults];
        setSearchResults(combinedResults);
      }

      setError('');
    } catch (err) {
      console.error('Error searching for reports:', err);
      setError('An error occurred while searching.');
    } finally {
      setLoading(false);
    }
  };

  // Function to check if the user is archived
  const checkIfUserIsArchived = async (userId) => {
    const userRef = doc(db, 'users', userId);
    const userDoc = await getDoc(userRef);
    if (userDoc.exists()) {
      return userDoc.data().archived === true;
    }
    return false;
  };

  const searchTraineeReports = async (collectionName, term) => {
    const reportsRef = collection(db, collectionName);

    // Get all reports for the trainee
    const traineeReportsQuery = query(
      reportsRef,
      where('traineeId', '==', currentUser.uid)
    );

    try {
      const traineeReportsSnapshot = await getDocs(traineeReportsQuery);

      // Filter reports by reportId or traineeName matching the term
      const results = traineeReportsSnapshot.docs
        .filter((docSnap) => {
          const data = docSnap.data();
          const reportIdMatches = data.reportId
            ?.toLowerCase()
            .includes(term);
          // Trainee name check is less relevant here as they only see their own
          return reportIdMatches; // Primarily search by ID for trainee
        })
        .map((docSnap) => ({
          id: docSnap.id,
          ...docSnap.data(),
          collection: collectionName,
        }));

      return results;
    } catch (error) {
      console.error(`Error searching ${collectionName}:`, error);
      return [];
    }
  };

  const searchInCollection = async (collectionName, term) => {
    const reportsRef = collection(db, collectionName);

    let traineeIdField = 'traineeId';
    let traineeNameField = 'traineeName';

    // Consider if limiting the initial fetch is okay or if a full text search solution is needed later
    const reportsQuery = query(reportsRef, limit(100)); // Increased limit slightly
    const reportsSnapshot = await getDocs(reportsQuery);
    const allDocs = reportsSnapshot.docs;

    // Fetch traineeIds from the reports
    const traineeIds = Array.from(
      new Set(allDocs.map((docSnap) => docSnap.data()[traineeIdField]).filter(Boolean)) // Filter out undefined IDs
    );

    // Fetch the users for these traineeIds if there are any
    let userDocs = [];
    if (traineeIds.length > 0) {
        const userDocsPromises = traineeIds.map((id) =>
          getDoc(doc(db, 'users', id)).catch(err => { console.error(`Failed to fetch user ${id}`, err); return null; }) // Handle potential errors fetching individual users
        );
        userDocs = (await Promise.all(userDocsPromises)).filter(Boolean); // Filter out nulls from errors
    }


    // Build a map of traineeId to archived status and name
    const traineeDataMap = {};
    userDocs.forEach((userDoc) => {
      if (userDoc.exists()) {
        const data = userDoc.data();
        traineeDataMap[userDoc.id] = { 
            archived: data.archived || false,
            name: data.name || 'Unknown'
        };
      }
    });

    // Filter out archived or non-matching reports
    const filteredDocs = allDocs
      .filter((docSnap) => {
        const data = docSnap.data();
        const traineeId = data[traineeIdField];
        
        // If traineeId exists, check if they are archived
        if (traineeId && traineeDataMap[traineeId]?.archived) {
          return false; 
        }

        const reportIdMatches = data.reportId
          ?.toLowerCase()
          .includes(term);
        
        // Use fetched trainee name if available, otherwise fallback
        const traineeName = traineeId ? (traineeDataMap[traineeId]?.name || 'Unknown') : (data[traineeNameField] || 'Unknown');
        const traineeNameMatches = traineeName
          .toLowerCase()
          .includes(term);
          
        return reportIdMatches || traineeNameMatches;
      })
      .map((docSnap) => {
          const data = docSnap.data();
          const traineeId = data[traineeIdField];
          const name = traineeId ? (traineeDataMap[traineeId]?.name || 'Unknown') : (data[traineeNameField] || 'Unknown');
          return {
            id: docSnap.id,
            ...data,
            collection: collectionName,
            traineeName: name, // Use determined name
        };
      });

    return filteredDocs;
  };

  const handleResultClick = (result) => {
    let path = '';
    if (result.collection === 'dors') {
      path = `/dor/${result.id}`;
    } else if (result.collection === 'endOfWeekReports') {
      path = `/endofweekreport/${result.id}`;
    } else if (result.collection === 'officerSafetyReports') {
      path = `/officersafetyreport/${result.id}`;
    }
    navigate(path);
  };

  const getReportTypeBadge = (collection) => {
    let color, label;

    switch (collection) {
      case 'dors':
        color = "blue";
        label = "Daily Observation";
        break;
      case 'endOfWeekReports':
        color = "green";
        label = "End of Week";
        break;
      case 'officerSafetyReports':
        color = "orange";
        label = "Officer Safety";
        break;
      default:
        color = "gray";
        label = "Report";
    }

    return (
      <Badge 
        colorScheme={color} 
        fontSize="0.6rem" 
        px={1.5} 
        py={0.5} 
        borderRadius="md"
      >
        {label}
      </Badge>
    );
  };

  // Get card background color based on theme
  const cardBg = isDarkMode ? "var(--color-primary)" : "var(--color-primary)";
  const resultBg = isDarkMode ? "var(--color-secondary)" : "var(--color-secondary)";
  const hoverBg = isDarkMode ? "rgba(0, 209, 219, 0.07)" : "rgba(0, 173, 181, 0.07)";
  const shadowBase = isDarkMode ? "var(--shadow-md)" : "var(--shadow-md)";
  const shadowHover = isDarkMode ? "var(--shadow-lg)" : "var(--shadow-lg)";

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6, ease: "easeOut" }}
    >
      <Box 
        p={{base: 3, md: 5}}
      >
        {/* Content Wrapper: Centered, with max-width */}
        <Box 
          w="100%" 
          maxW={{base: "100%", md: "500px"}} 
          mx="auto" // Center this block
        >
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            <Box mb={4}> {/* Title Container */}
              <Box
                fontSize={{base: "xl", md: "2xl"}} 
                fontWeight="bold" 
                className="text-text-light dark:text-text-dark"
                display="flex"
                alignItems="center"
                gap={2}
              >
                <motion.div
                  initial={{ rotate: 0 }}
                  animate={{ rotate: 360 }}
                  transition={{ duration: 0.6, delay: 0.4 }}
                >
                  <FiSearch />
                </motion.div>
                Search Reports
              </Box>
            </Box>
          </motion.div>
        
        <motion.div
          initial={{ opacity: 0, y: 30, scale: 0.95 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ duration: 0.5, delay: 0.4, ease: "easeOut" }}
        >
          <Card
            bg={cardBg}
            color={isDarkMode ? "var(--color-text)" : "var(--color-text)"}
            boxShadow={shadowBase}
            borderRadius="lg"
            overflow="hidden"
            border={`1px solid ${isDarkMode ? 'var(--color-secondary)' : 'var(--color-secondary)'}`}
            w="100%" // Card takes full width of its parent (the centered Box)
          >
          <CardBody p={{base: 3, md: 4}}>
            <Box mb={4}>
              <InputGroup size="md">
                <InputLeftElement pointerEvents="none">
                  <FiSearch color={isDarkMode ? "var(--color-text)" : "var(--color-text)"} />
                </InputLeftElement>
                <Input
                  placeholder={role === 'trainee' 
                    ? 'Search your reports by ID...' 
                    : 'Search by Report ID or Trainee Name...'
                  }
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  bg={isDarkMode ? "var(--color-secondary)" : "var(--color-secondary)"}
                  color={isDarkMode ? "var(--color-text)" : "var(--color-text)"}
                  borderColor={isDarkMode ? "var(--color-secondary)" : "var(--color-secondary)"}
                  _placeholder={{
                    color: isDarkMode ? "var(--color-text-dark)" : "var(--color-text-light)",
                    opacity: 0.6
                  }}
                  _focus={{
                    borderColor: isDarkMode ? "var(--color-accent)" : "var(--color-accent)",
                    boxShadow: `0 0 0 1px ${isDarkMode ? "var(--color-accent)" : "var(--color-accent)"}`
                  }}
                  aria-label="Search reports"
                />
                {loading && (
                  <Box position="absolute" right="10px" top="10px">
                    <Spinner size="sm" color={isDarkMode ? "var(--color-accent)" : "var(--color-accent)"} />
                  </Box>
                )}
              </InputGroup>
            </Box>

            {error && (
              <Alert status="error" borderRadius="md" mb={4} fontSize="sm">
                <AlertIcon />
                {error}
              </Alert>
            )}

            <AnimatePresence>
              {searchResults.length > 0 && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: "auto" }}
                  exit={{ opacity: 0, height: 0 }}
                  transition={{ duration: 0.3 }}
                >
                  <Box maxH="400px" overflowY="auto" pr={1}>
                    <Flex direction="column" gap={2}>
                      {searchResults.map((result, index) => (
                        <motion.div
                          key={`${result.collection}-${result.id}`}
                          initial={{ opacity: 0, x: -20, scale: 0.95 }}
                          animate={{ opacity: 1, x: 0, scale: 1 }}
                          transition={{ 
                            duration: 0.3, 
                            delay: index * 0.1,
                            ease: "easeOut"
                          }}
                          whileHover={{ 
                            scale: 1.02, 
                            y: -2,
                            transition: { duration: 0.2 }
                          }}
                          whileTap={{ scale: 0.98 }}
                        >
                          <Box 
                            onClick={() => handleResultClick(result)}
                            bg={resultBg}
                            p={3}
                            borderRadius="md"
                            cursor="pointer"
                            transition="all 0.2s"
                            _hover={{
                              bg: hoverBg,
                              boxShadow: shadowHover
                            }}
                            boxShadow="var(--shadow-sm)"
                            border={`1px solid ${isDarkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)'}`}
                          >
                      <Flex justify="space-between" mb={1} align="center">
                        <Flex align="center" gap={1.5} flex="1" minW={0}>
                          <FiHash size="14px" />
                          <Text fontWeight="medium" fontSize="sm" noOfLines={1}>
                            {result.reportId || 'No Report ID'}
                          </Text>
                        </Flex>
                        {getReportTypeBadge(result.collection)}
                      </Flex>
                      
                      <Flex align="center" gap={1.5} mt={2}>
                        <FiUser size="12px" opacity={0.7} />
                        <Text fontSize="xs" opacity={0.8} noOfLines={1}>
                          {result.traineeName}
                        </Text>
                      </Flex>
                      
                      {result.date && (
                        <Flex align="center" gap={1.5} mt={1}>
                          <FiCalendar size="12px" opacity={0.7} />
                          <Text fontSize="xs" opacity={0.8}>
                            {typeof result.date === 'string' 
                              ? result.date 
                              : result.date.toDate?.()?.toLocaleDateString() || 'No date'}
                          </Text>
                        </Flex>
                      )}
                    </Box>
                          </motion.div>
                        ))}
                      </Flex>
                    </Box>
                  </motion.div>
                )}
            </AnimatePresence>

            <AnimatePresence>
              {searchTerm && !loading && searchResults.length === 0 && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  transition={{ duration: 0.3 }}
                >
                  <Flex 
                    direction="column" 
                    align="center" 
                    justify="center" 
                    py={6} 
                    opacity={0.7}
                  >
                    <motion.div
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      transition={{ duration: 0.3, delay: 0.1 }}
                    >
                      <FiInfo size="24px" />
                    </motion.div>
                    <Text mt={2} fontSize="sm" textAlign="center">
                      No reports found matching your search.
                    </Text>
                  </Flex>
                </motion.div>
              )}
            </AnimatePresence>
            
            <AnimatePresence>
              {!searchTerm && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  transition={{ duration: 0.3 }}
                >
                  <Flex 
                    direction="column" 
                    align="center" 
                    justify="center" 
                    py={6} 
                    opacity={0.7}
                  >
                    <motion.div
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      transition={{ duration: 0.3, delay: 0.1 }}
                    >
                      <FiSearch size="24px" />
                    </motion.div>
                    <Text mt={2} fontSize="sm" textAlign="center">
                      Type to search for reports
                    </Text>
                  </Flex>
                </motion.div>
              )}
            </AnimatePresence>
          </CardBody>
        </Card>
        </motion.div>
      </Box>
    </Box>
    </motion.div>
  );
};

export default SearchReports;
